/**
 * TourProductionDemo — mock packer production UI shown during tour walkthrough.
 * Mirrors the desktop embedded layout of PackerWeightEntry (3-column grid)
 * with hardcoded demo data and data-tour attributes for Joyride targeting.
 *
 * activeStep controls which mock view is shown:
 *   - 'scanning'    → weight entry + print label (session info, box scanning, print)
 *   - 'badge-scan'  → badge + label scan confirmation
 *   - 'finish'      → target reached modal with finish button
 *   - 'waste'       → simplified waste tracking wizard overview
 */
import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import {
  Printer,
  CheckCircle,
  Package,
  Menu,
  User,
  QrCode,
  Trophy,
  Trash2,
  Recycle,
  AlertTriangle,
  ClipboardCheck,
  ShieldCheck,
} from 'lucide-react';

export type TourProductionStep = 'scanning' | 'badge-scan' | 'finish' | 'waste';

interface TourProductionDemoProps {
  activeStep: TourProductionStep;
}

/* ─── Badge + Label Scan Confirmation View ──────────────────────── */
const BadgeLabelScanView: React.FC<{ t: (key: string) => string }> = ({ t }) => (
  <div className="flex-1 flex flex-col items-center justify-center p-8">
    <div data-tour="mock-badge-label-scan" className="w-full max-w-md space-y-4">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-1">
        Confirm Label Applied
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
        Scan badge then label to confirm
      </p>

      {/* Badge card — completed */}
      <div className="border-2 border-green-500 bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="font-semibold text-green-700 dark:text-green-300">1. Scan Your Badge</p>
            <p className="text-sm text-green-600 dark:text-green-400">@1234 — Demo Worker</p>
          </div>
        </div>
      </div>

      {/* Label card — waiting (amber pulse) */}
      <div className="border-2 border-amber-500 bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 animate-pulse">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
            <QrCode className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <p className="font-semibold text-amber-700 dark:text-amber-300">2. Scan Box Label</p>
            <p className="text-sm text-amber-600 dark:text-amber-400">Waiting...</p>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <input
            readOnly
            className="flex-1 border border-amber-300 dark:border-amber-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-500"
            placeholder="#BOX-..."
          />
          <button className="px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium">Go</button>
        </div>
      </div>
    </div>
  </div>
);

/* ─── Target Reached / Finish Session View ──────────────────────── */
const FinishSessionView: React.FC<{ t: (key: string) => string }> = ({ t }) => (
  <div className="flex-1 flex flex-col items-center justify-center p-8">
    <div data-tour="mock-finish-session" className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 text-center">
      {/* Trophy icon */}
      <div className="mx-auto w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
        <Trophy className="w-10 h-10 text-green-600 dark:text-green-400" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        {t('mobile.targetReached') || 'Target Reached!'}
      </h2>
      <p className="text-gray-500 dark:text-gray-400 mb-4">
        You've packed all the target boxes for this session
      </p>
      <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-6">
        20 / 20 {t('mobile.boxes') || 'boxes'}
      </div>
      <div className="flex gap-3">
        <button className="flex-1 py-3 rounded-xl font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600">
          Continue Packing
        </button>
        <button className="flex-1 py-3 rounded-xl font-bold text-white bg-green-600">
          Complete Session
        </button>
      </div>
    </div>
  </div>
);

/* ─── Waste Tracking Overview View ──────────────────────────────── */
const WasteTrackingView: React.FC<{ t: (key: string) => string }> = ({ t }) => (
  <div className="flex-1 flex flex-col p-6">
    <div className="max-w-2xl mx-auto w-full">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
        Waste Tracking
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
        Record all waste and byproducts before completing
      </p>

      {/* Step cards */}
      <div data-tour="mock-waste-tracking" className="space-y-3">
        {/* Disposable */}
        <div className="flex items-center gap-3 p-3 rounded-xl border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <div className="w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-red-700 dark:text-red-300 text-sm">Disposable Waste</p>
            <p className="text-xs text-red-500 dark:text-red-400">Material that cannot be used</p>
          </div>
          <span className="font-bold text-red-600 dark:text-red-400">2.5 lbs</span>
        </div>

        {/* Human Error */}
        <div className="flex items-center gap-3 p-3 rounded-xl border-2 border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20">
          <div className="w-9 h-9 rounded-full bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-teal-600 dark:text-teal-400" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-teal-700 dark:text-teal-300 text-sm">Human Error</p>
            <p className="text-xs text-teal-500 dark:text-teal-400">Tracking only — doesn't count toward waste %</p>
          </div>
          <span className="font-bold text-teal-600 dark:text-teal-400">0.5 lbs</span>
        </div>

        {/* By-Product */}
        <div className="flex items-center gap-3 p-3 rounded-xl border-2 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
          <div className="w-9 h-9 rounded-full bg-yellow-100 dark:bg-yellow-900/40 flex items-center justify-center">
            <Recycle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-yellow-700 dark:text-yellow-300 text-sm">By-Products</p>
            <p className="text-xs text-yellow-500 dark:text-yellow-400">Marketable or reusable materials</p>
          </div>
          <span className="font-bold text-yellow-600 dark:text-yellow-400">5.0 lbs</span>
        </div>

        {/* Remainder */}
        <div className="flex items-center gap-3 p-3 rounded-xl border-2 border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20">
          <div className="w-9 h-9 rounded-full bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
            <Package className="w-4 h-4 text-orange-600 dark:text-orange-400" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-orange-700 dark:text-orange-300 text-sm">Remainder</p>
            <p className="text-xs text-orange-500 dark:text-orange-400">Intact boxes & damaged material</p>
          </div>
          <span className="font-bold text-orange-600 dark:text-orange-400">1.0 lbs</span>
        </div>

        {/* Summary */}
        <div className="flex items-center gap-3 p-3 rounded-xl border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
            <ClipboardCheck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-blue-700 dark:text-blue-300 text-sm">Summary & Review</p>
            <p className="text-xs text-blue-500 dark:text-blue-400">Review all waste entries before submitting</p>
          </div>
        </div>

        {/* Supervisor Sign-Off */}
        <div className="flex items-center gap-3 p-3 rounded-xl border-2 border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20">
          <div className="w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-purple-700 dark:text-purple-300 text-sm">Supervisor Sign-Off</p>
            <p className="text-xs text-purple-500 dark:text-purple-400">Supervisor scans badge to approve</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ─── Main Component ────────────────────────────────────────────── */
const TourProductionDemo: React.FC<TourProductionDemoProps> = ({ activeStep }) => {
  const { t } = useLanguage();

  // Badge scan, finish, and waste views replace the entire layout
  if (activeStep === 'badge-scan') {
    return (
      <div className="flex flex-col bg-gray-100 dark:bg-gray-900 min-h-[calc(100vh-5rem)]">
        <BadgeLabelScanView t={t} />
      </div>
    );
  }

  if (activeStep === 'finish') {
    return (
      <div className="flex flex-col bg-gray-100 dark:bg-gray-900 min-h-[calc(100vh-5rem)]">
        <FinishSessionView t={t} />
      </div>
    );
  }

  if (activeStep === 'waste') {
    return (
      <div className="flex flex-col bg-gray-100 dark:bg-gray-900 min-h-[calc(100vh-5rem)]">
        <WasteTrackingView t={t} />
      </div>
    );
  }

  // Default: scanning view (session info + weight entry + print)
  return (
    <div className="flex flex-col bg-gray-100 dark:bg-gray-900 min-h-[calc(100vh-5rem)]">
      {/* Top Bar - Actions & Timer (matches MobilePackerView embedded top bar) */}
      <div className="px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 text-base font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg">
            <Menu className="w-5 h-5" />
            Actions
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className="font-mono text-lg font-bold text-gray-900 dark:text-white">0:12:34</div>
        </div>
      </div>

      {/* Packing Instructions bar (desktop 2-line layout) */}
      <div data-tour="mock-session-info" className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="hidden md:block md:px-6 pb-3 pt-3 text-sm space-y-1">
          <div className="flex items-center gap-x-3 flex-wrap">
            <span className="text-xs font-semibold uppercase text-amber-600 dark:text-amber-400">Finished Product:</span>
            <span className="font-bold text-gray-900 dark:text-white">DEMO-SKU</span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="text-gray-700 dark:text-gray-300">Demo Product</span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="font-medium text-gray-900 dark:text-white">10 x 1 lb</span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="font-bold text-amber-600 dark:text-amber-400">20 {t('mobile.boxes') || 'boxes'}</span>
          </div>
          <div className="flex items-center gap-x-3 flex-wrap text-gray-500 dark:text-gray-400">
            <span className="text-xs font-semibold uppercase text-blue-600 dark:text-blue-400">Raw Material:</span>
            <span className="font-medium text-gray-700 dark:text-gray-300">RM-001</span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="font-medium text-gray-700 dark:text-gray-300">Demo Raw Material</span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span>Lot: <span className="font-medium text-gray-700 dark:text-gray-300">WT LOT-001</span></span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span>10.0 lbs/box</span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span>250 lbs initial</span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span>Yield: <span className="font-medium text-gray-700 dark:text-gray-300">92.00%</span></span>
          </div>
        </div>
      </div>

      {/* Main Content - 3 column grid (matches PackerWeightEntry embedded layout) */}
      <div className="flex-1 flex flex-col p-4 md:mx-auto md:w-full">
        <div className="flex-1 grid grid-cols-3 gap-4">
          {/* Left column: Progress card + Packing Info card */}
          <div className="col-span-1 flex flex-col gap-4">
            {/* Progress Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Station 1</span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">DEMO-001</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600 dark:text-gray-400">Demo Worker</span>
                <span className="text-sm text-gray-400 dark:text-gray-500">#1234</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-500">{t('mobile.progress') || 'Progress'}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  7 / 20 {t('mobile.boxes') || 'boxes'}
                </span>
              </div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full transition-all duration-300 bg-amber-500" style={{ width: '35%' }} />
              </div>
            </div>

            {/* Packing Info Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('mobile.packingInstructions') || 'Packing Instructions'}
                </h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="text-xs font-semibold uppercase text-amber-600 dark:text-amber-400">Finished Product</div>
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <span className="text-gray-500">Item #:</span>
                  <span className="font-bold text-gray-900 dark:text-white">DEMO-SKU</span>
                  <span className="text-gray-500">Name:</span>
                  <span className="text-gray-900 dark:text-white truncate">Demo Product</span>
                  <span className="text-gray-500">Packing:</span>
                  <span className="text-gray-900 dark:text-white">10 x 1 lb</span>
                  <span className="text-gray-500">Expected:</span>
                  <span className="font-bold text-amber-600 dark:text-amber-400">20 boxes</span>
                </div>
              </div>
              <div className="my-3 border-t border-gray-200 dark:border-gray-700" />
              <div className="space-y-2 text-sm">
                <div className="text-xs font-semibold uppercase text-blue-600 dark:text-blue-400">Raw Material</div>
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <span className="text-gray-500">Item #:</span>
                  <span className="font-medium text-gray-900 dark:text-white">RM-001</span>
                  <span className="text-gray-500">Name:</span>
                  <span className="text-gray-900 dark:text-white truncate">Demo Raw Material</span>
                  <span className="text-gray-500">Lot:</span>
                  <span className="text-gray-900 dark:text-white">WT LOT-001</span>
                  <span className="text-gray-500">Unit Wt:</span>
                  <span className="text-gray-900 dark:text-white">10.0 lbs/box</span>
                  <span className="text-gray-500">Yield:</span>
                  <span className="text-gray-900 dark:text-white">92.00%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right column: Weight + Print card (spans 2 cols) */}
          <div className="col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 flex flex-col items-center justify-center">
            <div className="w-full max-w-lg">
              {/* Catch Weight mode display */}
              <div className="text-center mb-2">
                <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                  {t('mobile.catchWeight') || 'Catch Weight'}
                </span>
              </div>
              <div data-tour="mock-box-scanning" className="relative">
                <div className="w-full text-center text-6xl md:text-7xl font-bold py-6 px-4 rounded-2xl border-4 bg-white dark:bg-gray-800 border-green-500 text-green-600 dark:text-green-400">
                  10.25
                </div>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400 font-medium">lbs</span>
              </div>
              <div className="mt-3 text-center">
                <p className="text-xs text-gray-400 mb-1">
                  {t('mobile.weightWindow') || 'Weight Window'}: 9.50 - 10.50 lbs
                </p>
                <p className="text-green-600 dark:text-green-400 flex items-center justify-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {t('mobile.weightValid') || 'Weight is within range'}
                </p>
              </div>
            </div>

            {/* Print Button */}
            <button
              data-tour="mock-print-label"
              className="w-full max-w-lg mt-3 py-4 rounded-xl font-bold text-xl flex items-center justify-center gap-3 bg-blue-600 text-white"
            >
              <Printer className="w-6 h-6" />
              {t('mobile.printLabel') || 'Print Label'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourProductionDemo;
